import React, { useEffect, useRef } from "react"
import * as Chart from "chart.js"
import { translations } from "../../utils/Translation/staticTranslation"

const SubscriptionChart = ({ data }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  const lang =
    typeof window !== "undefined"
      ? localStorage.getItem("language") || "ar"
      : "ar"
  const dashboardAdmin = translations[lang]?.DashboardSuperAdmin

  useEffect(() => {
    if (!data?.plan_usage) return

    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const ctx = chartRef.current.getContext("2d")
    const planUsage = data.plan_usage

    // ✅ Use localized labels based on language
    const baseLabels =
      lang === "ar"
        ? Object.values(planUsage).map((p) => p.plan_ar)
        : Object.values(planUsage).map((p) => p.plan_en)

    const baseValues = Object.keys(planUsage).map((k) => planUsage[k].count)
    const total = baseValues.reduce((s, v) => s + v, 0)
    const isNoData = total === 0

    const palette = ["#774bcf", "#318aa3", "#61e98e", "#fb6515", "#000000"]

    // Map plan names to specific colors
    const planColorMap = {
      "Free Trial": "#774bcf",
      Starter: "#318aa3",
      Basic: "#61e98e",
      Flex: "#3268DE",
      Pro: "#fb6515",
      Business: "#000000",
    }

    const labels = isNoData
      ? [lang === "ar" ? "لا توجد بيانات" : "No Data"]
      : baseLabels
    const values = isNoData ? [1] : baseValues

    // Assign colors based on plan names
    const colors = isNoData
      ? ["#E5E7EB"]
      : Object.keys(planUsage).map(
          (key) =>
            planColorMap[key] ||
            palette[Object.keys(planUsage).indexOf(key) % palette.length]
        )

    const innerLabelsPlugin = {
      id: "innerLabelsOnlyForBigSlices",
      afterDatasetDraw(chart) {
        if (isNoData) return
        const { ctx, data } = chart
        const meta = chart.getDatasetMeta(0)
        ctx.save()
        const sum = data.datasets[0].data.reduce((a, b) => a + b, 0)

        data.datasets[0].data.forEach((val, i) => {
          const pct = sum ? (val / sum) * 100 : 0
          if (pct < 10) return
          const arc = meta.data[i]
          if (!arc) return
          const mid = (arc.startAngle + arc.endAngle) / 2
          const r = (arc.outerRadius + arc.innerRadius) / 2
          const x = arc.x + Math.cos(mid) * r
          const y = arc.y + Math.sin(mid) * r
          ctx.fillStyle = "#ffffff"
          ctx.font = "bold 13px Arial"
          ctx.textAlign = "center"
          ctx.textBaseline = "middle"
          ctx.fillText(data.labels[i], x, y)
        })
        ctx.restore()
      },
    }

    const noDataCenterText = {
      id: "noDataCenterText",
      afterDraw(chart) {
        if (!isNoData) return
        const { ctx, chartArea } = chart
        const cx = (chartArea.left + chartArea.right) / 2
        const cy = (chartArea.top + chartArea.bottom) / 2
        ctx.save()
        ctx.fillStyle = "#6B7280"
        ctx.font = "bold 16px Arial"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(lang === "ar" ? "لا توجد خطط" : "No Plans", cx, cy)
        ctx.restore()
      },
    }

    chartInstance.current = new Chart.Chart(ctx, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: "#ffffff",
            borderWidth: 2,
            hoverOffset: 16,
          },
        ],
      },
      options: {
        cutoutPercentage: 65,
        rotation: -90,
        responsive: true,
        maintainAspectRatio: true,
        layout: {
          padding: { top: 20, bottom: 20, left: 20, right: 20 },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            callbacks: {
              label: function (context) {
                if (isNoData) return lang === "ar" ? "لا توجد خطط" : "No Plans"

                const key = Object.keys(planUsage)[context.dataIndex]
                const label =
                  lang === "ar"
                    ? planUsage[key]?.plan_ar
                    : planUsage[key]?.plan_en
                const count = planUsage[key]?.count
                const pct =
                  planUsage[key]?.percentage != null
                    ? Number(planUsage[key].percentage).toFixed(2)
                    : total
                    ? ((count / total) * 100).toFixed(2)
                    : "0.00"
                return `${label}: ${count} (${pct}%)`
              },
            },
          },
        },
      },
      plugins: [innerLabelsPlugin, noDataCenterText],
    })

    return () => {
      if (chartInstance.current) chartInstance.current.destroy()
    }
  }, [data, lang])

  return (
    <div
      className="h-100 w-100"
      style={{
        display: "grid",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: "358px",
      }}
    >
      <div
        className="relative mx-auto"
        style={{ height: "340px", width: "340px", overflow: "visible" }}
      >
        <canvas ref={chartRef} style={{ overflow: "visible" }} />
      </div>
    </div>
  )
}

export default SubscriptionChart
